import { required, useTranslate, type OptionText } from "ra-core";
import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

import { contactOptionText } from "../misc/ContactOption";
import type { Task } from "../types";

type TaskTargetMode = "contact" | "company" | "deal";

type TaskTargetSource = "contact_id" | "company_id" | "deal_id";

interface TaskTargetField {
  source: TaskTargetSource;
  reference: string;
  label: string;
  optionText?: OptionText;
}

/** One mode, one foreign key, one reference to pick it from. */
const TARGET_FIELDS: Record<TaskTargetMode, TaskTargetField> = {
  contact: {
    source: "contact_id",
    reference: "contacts_summary",
    label: "resources.tasks.fields.contact_id",
    optionText: contactOptionText,
  },
  company: {
    source: "company_id",
    reference: "companies",
    label: "resources.tasks.fields.company_id",
  },
  deal: {
    source: "deal_id",
    reference: "deals",
    label: "resources.tasks.fields.deal_id",
  },
};

const TARGET_MODES = Object.keys(TARGET_FIELDS) as TaskTargetMode[];

/**
 * The target a task was EXPLICITLY aimed at.
 *
 * `company_id` is derived by the `set_task_company` trigger -- from the deal
 * when there is one, else from the contact -- so its presence alone proves
 * nothing: a plain contact task carries a derived `company_id` too. The only
 * `company_id` a user actually chose is one on a task with NO `contact_id`,
 * and that is how an explicit account task is told apart from a derived one.
 * So "the most specific target wins" (deal, then company, then contact) is
 * read here as: deal first, then a contact if there is one, and only then a
 * contact-less company.
 */
const getTaskTargetMode = (
  task: Pick<Task, "contact_id" | "company_id" | "deal_id">,
): TaskTargetMode | undefined => {
  if (task.deal_id != null) return "deal";
  if (task.contact_id != null) return "contact";
  if (task.company_id != null) return "company";
  return undefined;
};

/**
 * Chooses what a task is about: a person, an account, or a deal.
 *
 * Picking a mode clears the two other foreign keys, so exactly one target is
 * ever submitted: the `tasks_has_target` check constraint cannot be violated
 * from the form, and `company_id` is left to the trigger to derive for a
 * contact or deal task.
 */
export const TaskTargetInput = () => {
  const translate = useTranslate();
  const { control, setValue } = useFormContext<Task>();
  const contactId = useWatch({ control, name: "contact_id" });
  const companyId = useWatch({ control, name: "company_id" });
  const dealId = useWatch({ control, name: "deal_id" });
  const [pickedMode, setPickedMode] = useState<TaskTargetMode | null>(null);

  /*
    When editing, the record lands after the first render, so the mode follows
    the form values until the user picks one; from then on the pick wins. A
    brand new task has no target yet and starts on the person, which is what a
    task was before an account could own one.
  */
  const mode =
    pickedMode ??
    getTaskTargetMode({
      contact_id: contactId,
      company_id: companyId,
      deal_id: dealId,
    }) ??
    "contact";
  const target = TARGET_FIELDS[mode];

  const handleModeChange = (value: string) => {
    const nextMode = value as TaskTargetMode;
    setPickedMode(nextMode);
    TARGET_MODES.filter((candidate) => candidate !== nextMode).forEach(
      (candidate) =>
        setValue(TARGET_FIELDS[candidate].source, null, { shouldDirty: true }),
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <fieldset>
        <legend className="text-sm font-medium mb-2">
          {translate("resources.tasks.target.legend")}
        </legend>
        <RadioGroup
          value={mode}
          onValueChange={handleModeChange}
          className="flex flex-row flex-wrap gap-4"
        >
          {TARGET_MODES.map((candidate) => (
            <div key={candidate} className="flex items-center gap-2">
              <RadioGroupItem
                value={candidate}
                id={`task-target-${candidate}`}
              />
              <Label
                htmlFor={`task-target-${candidate}`}
                className="text-sm font-normal cursor-pointer"
              >
                {translate(`resources.tasks.target.${candidate}`)}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </fieldset>
      <p className="text-xs text-muted-foreground">
        {translate("resources.tasks.target.help")}
      </p>

      {/* Remounted per mode so the autocomplete never keeps the previous
          target's search term. */}
      <ReferenceInput
        key={target.source}
        source={target.source}
        reference={target.reference}
      >
        <AutocompleteInput
          label={target.label}
          optionText={target.optionText}
          helperText={false}
          validate={required("resources.tasks.target.required")}
          modal
        />
      </ReferenceInput>
    </div>
  );
};
